import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { equal } from "@wry/equality";
import * as React from "react";

import type {
  ApolloClient,
  DefaultContext,
  DocumentNode,
  ErrorLike,
  ErrorPolicy,
  InternalRefetchQueriesInclude,
  MaybeMasked,
  MutationFetchPolicy,
  MutationQueryReducersMap,
  MutationUpdaterFunction,
  NormalizedExecutionResult,
  OnQueryUpdated,
  OperationVariables,
  Unmasked,
} from "@apollo/client";
import type { Cache, IgnoreModifier } from "@apollo/client/cache";
import type {
  LazyType,
  OptionWithFallback,
  Prettify,
  SignatureStyle,
} from "@apollo/client/utilities/internal";
import {
  mergeOptions,
  preventUnhandledRejection,
} from "@apollo/client/utilities/internal";

import { useIsomorphicLayoutEffect } from "./internal/useIsomorphicLayoutEffect.js";
import { useApolloClient } from "./useApolloClient.js";

type MakeRequiredVariablesOptional<
  TVariables extends OperationVariables,
  TConfiguredVariables extends Partial<TVariables>,
> = Prettify<
  {
    [K in keyof TVariables as K extends keyof TConfiguredVariables ? K
    : never]?: TVariables[K];
  } & Omit<TVariables, keyof TConfiguredVariables>
>;

// Extract the `variables` value from an inferred `TOptions`. We use a keyof
// guard (rather than `TOptions extends { variables: infer V }`) because
// `variables` in the modern signature's constraint is declared optional, so
// the `extends { variables: infer V }` form fails to match when the user
// passes a complex options object that gets widened to include the optional
// modifier. `TOptions["variables"]` works for both required and optional
// `variables` entries.
type ExtractConfiguredVariables<
  TOptions,
  TVariables extends OperationVariables,
> = "variables" extends keyof TOptions ?
  Exclude<TOptions["variables"], undefined> extends infer V ?
    [V] extends [never] ? {}
    : V extends Partial<TVariables> ? V
    : {}
  : {}
: {};

export declare namespace useMutation {
  export interface Options<
    TData = unknown,
    TVariables extends OperationVariables = OperationVariables,
    TCache extends Cache.Implementation = Cache.Implementation,
    TConfiguredVariables extends Partial<TVariables> = Partial<TVariables>,
  > {
    /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#optimisticResponse:member} */
    optimisticResponse?:
      | Unmasked<NoInfer<TData>>
      | ((
          vars: TVariables,
          { IGNORE }: { IGNORE: IgnoreModifier }
        ) => Unmasked<NoInfer<TData>> | IgnoreModifier);

    /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#updateQueries:member} */
    updateQueries?: MutationQueryReducersMap<TData>;

    /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#refetchQueries:member} */
    refetchQueries?:
      | ((
          result: NormalizedExecutionResult<Unmasked<TData>>
        ) => InternalRefetchQueriesInclude)
      | InternalRefetchQueriesInclude;

    /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#awaitRefetchQueries:member} */
    awaitRefetchQueries?: boolean;

    /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#update:member} */
    update?: MutationUpdaterFunction<TData, TVariables, TCache>;

    /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#onQueryUpdated:member} */
    onQueryUpdated?: OnQueryUpdated<any>;

    /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#errorPolicy:member} */
    errorPolicy?: ErrorPolicy;

    /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#variables:member} */
    variables?: Partial<TVariables> & TConfiguredVariables;

    /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#context:member} */
    context?: DefaultContext;

    /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#fetchPolicy:member} */
    fetchPolicy?: MutationFetchPolicy;

    /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#keepRootFields:member} */
    keepRootFields?: boolean;

    /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#client:member} */
    client?: ApolloClient;

    /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#notifyOnNetworkStatusChange:member} */
    notifyOnNetworkStatusChange?: boolean;

    /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#onCompleted:member} */
    onCompleted?: (
      data: MaybeMasked<TData>,
      clientOptions?: Options<TData, TVariables, TCache>
    ) => void;

    /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#onError:member} */
    onError?: (
      error: ErrorLike,
      clientOptions?: Options<TData, TVariables, TCache>
    ) => void;
  }

  export namespace Base {
    export interface Result {
      /** {@inheritDoc @apollo/client!MutationResultDocumentation#loading:member} */
      loading: boolean;

      /** {@inheritDoc @apollo/client!MutationResultDocumentation#called:member} */
      called: boolean;

      /** {@inheritDoc @apollo/client!MutationResultDocumentation#client:member} */
      client: ApolloClient;

      /** {@inheritDoc @apollo/client!MutationResultDocumentation#reset:member} */
      reset: () => void;
    }
  }

  /**
   * Maps `errorPolicy` to the shape of `data` and `error` as observable from
   * the hook result state.
   *
   * The hook has additional states (before call, during loading) where `data`
   * and `error` are `undefined`, so `data` remains nullable even on error
   * policies that would otherwise guarantee it. Only `error` is narrowed away
   * for `"ignore"`, since the underlying `client.mutate` promise never rejects
   * and never resolves with an error for that policy.
   */
  export type ResultStateMap<TData = unknown> = {
    none: {
      /** {@inheritDoc @apollo/client!MutationResultDocumentation#data:member} */
      data: MaybeMasked<TData> | null | undefined;

      /** {@inheritDoc @apollo/client!MutationResultDocumentation#error:member} */
      error: ErrorLike | undefined;
    };
    all: {
      /** {@inheritDoc @apollo/client!MutationResultDocumentation#data:member} */
      data: MaybeMasked<TData> | null | undefined;

      /** {@inheritDoc @apollo/client!MutationResultDocumentation#error:member} */
      error: ErrorLike | undefined;
    };
    ignore: {
      /** {@inheritDoc @apollo/client!MutationResultDocumentation#data:member} */
      data: MaybeMasked<TData> | null | undefined;

      /** {@inheritDoc @apollo/client!MutationResultDocumentation#error:member} */
      error: undefined;
    };
    undefined: {
      /** {@inheritDoc @apollo/client!MutationResultDocumentation#data:member} */
      data: MaybeMasked<TData> | null | undefined;

      /** {@inheritDoc @apollo/client!MutationResultDocumentation#error:member} */
      error: ErrorLike | undefined;
    };
  };

  export type Result<
    TData = unknown,
    TErrorPolicy extends ErrorPolicy | undefined = undefined,
  > = Base.Result & ResultStateMap<TData>[`${TErrorPolicy}`];

  export namespace DocumentationTypes {
    namespace useMutation {
      export interface Result<TData = unknown> extends Base.Result {
        /** {@inheritDoc @apollo/client!MutationResultDocumentation#data:member} */
        data: MaybeMasked<TData> | null | undefined;

        /** {@inheritDoc @apollo/client!MutationResultDocumentation#error:member} */
        error: ErrorLike | undefined;
      }
    }
  }

  export type ResultTuple<
    TData,
    TVariables extends OperationVariables,
    TCache extends Cache.Implementation = Cache.Implementation,
    TErrorPolicy extends ErrorPolicy | undefined = undefined,
  > = [
    mutate: MutationFunction<TData, TVariables, TCache, TErrorPolicy>,
    result: Result<TData, TErrorPolicy>,
  ];

  export type MutationFunction<
    TData,
    TVariables extends OperationVariables,
    TCache extends Cache.Implementation = Cache.Implementation,
    TErrorPolicy extends ErrorPolicy | undefined = undefined,
  > = (
    ...[options]: {} extends TVariables ?
      [
        options?: MutationFunctionOptions<TData, TVariables, TCache> & {
          /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#variables:member} */
          variables?: TVariables;
        },
      ]
    : [
        options: MutationFunctionOptions<TData, TVariables, TCache> & {
          /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#variables:member} */
          variables: TVariables;
        },
      ]
  ) => Promise<ApolloClient.MutateResult<MaybeMasked<TData>, TErrorPolicy>>;

  export type MutationFunctionOptions<
    TData = unknown,
    TVariables extends OperationVariables = OperationVariables,
    TCache extends Cache.Implementation = Cache.Implementation,
  > = Options<TData, TVariables, TCache> & {
    /**
     * {@inheritDoc @apollo/client!MutationOptionsDocumentation#context:member}
     *
     * @remarks
     * When provided as a callback function, the function is called with the
     * value of `context` provided to the `useMutation` hook.
     */
    context?:
      | DefaultContext
      | ((hookContext: DefaultContext | undefined) => DefaultContext);
  };

  export interface DefaultOptions
    extends ApolloClient.DefaultOptions.Mutate.Calculated {}

  export type OptionsFor<TOptions> =
    Exclude<
      keyof TOptions,
      keyof useMutation.Options<any, any, any, any>
    > extends infer InvalidOptionNames extends string ?
      [InvalidOptionNames] extends [never] ?
        unknown
      : { [K in InvalidOptionNames]: never }
    : never;

  export type ResultForOptions<
    TData,
    TVariables extends OperationVariables,
    TCache extends Cache.Implementation,
    TOptions extends Record<string, never> | Options<TData, TVariables, TCache>,
    TErrorPolicy extends ErrorPolicy | undefined = undefined,
  > = LazyType<
    ResultTuple<
      TData,
      MakeRequiredVariablesOptional<
        TVariables,
        ExtractConfiguredVariables<TOptions, TVariables>
      >,
      TCache,
      OptionWithFallback<
        { errorPolicy: TErrorPolicy },
        DefaultOptions,
        "errorPolicy"
      > &
        ErrorPolicy
    >
  >;

  export namespace DocumentationTypes {
    /**
     * > Refer to the [Mutations](https://www.apollographql.com/docs/react/data/mutations/) section for a more in-depth overview of `useMutation`.
     *
     * @example
     *
     * ```jsx
     * import { gql, useMutation } from "@apollo/client";
     *
     * const ADD_TODO = gql`
     *   mutation AddTodo($type: String!) {
     *     addTodo(type: $type) {
     *       id
     *       type
     *     }
     *   }
     * `;
     *
     * function AddTodo() {
     *   let input;
     *   const [addTodo, { data }] = useMutation(ADD_TODO);
     *
     *   return (
     *     <div>
     *       <form
     *         onSubmit={(e) => {
     *           e.preventDefault();
     *           addTodo({ variables: { type: input.value } });
     *           input.value = "";
     *         }}
     *       >
     *         <input
     *           ref={(node) => {
     *             input = node;
     *           }}
     *         />
     *         <button type="submit">Add Todo</button>
     *       </form>
     *     </div>
     *   );
     * }
     * ```
     *
     * @param mutation - A GraphQL mutation document parsed into an AST by `gql`.
     * @param options - Options to control how the mutation is executed.
     * @returns A tuple in the form of `[mutate, result]`
     */
    export interface useMutation {
      <
        TData = unknown,
        TVariables extends OperationVariables = OperationVariables,
      >(
        mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
        options?: Options<TData, TVariables>
      ): ResultTuple<TData, TVariables>;
    }
  }

  export namespace Signatures {
    /** {@inheritDoc @apollo/client/react!useMutation.DocumentationTypes.useMutation:call(1)} */
    export interface Classic {
      // _INFERENCE_ONLY_DO_NOT_SPECIFY is used to distinguish between inferred
      // generics arguments and explicit generic arguments so that we can
      // provide a `@deprecated` signature for explicit generic arguments. As
      // soon as a user provides a generic arg (e.g. useMutation<TData>(mutation))`,
      // the overload falls through to the overloads without
      // _INFERENCE_ONLY_DO_NOT_SPECIFY.

      /** {@inheritDoc @apollo/client/react!useMutation.DocumentationTypes.useMutation:call(1)} */
      <
        TData,
        TVariables extends OperationVariables,
        _INFERENCE_ONLY_DO_NOT_SPECIFY extends "inferred",
        TConfiguredVariables extends Partial<TVariables> = {},
        TErrorPolicy extends ErrorPolicy | undefined = undefined,
      >(
        mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
        options?: useMutation.Options<
          NoInfer<TData>,
          NoInfer<TVariables>,
          Cache.Implementation,
          {
            [K in keyof TConfiguredVariables]: K extends keyof TVariables ?
              TConfiguredVariables[K]
            : never;
          }
        > & {
          /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#errorPolicy:member} */
          errorPolicy?: TErrorPolicy;
        }
      ): useMutation.ResultTuple<
        TData,
        MakeRequiredVariablesOptional<TVariables, TConfiguredVariables>,
        Cache.Implementation,
        TErrorPolicy
      >;

      /**
       * @deprecated Avoid manually specifying generics on `useMutation`.
       * Instead, rely on TypeScript's type inference along with a correctly typed `TypedDocumentNode` to get accurate types for your mutation results.
       *
       * {@inheritDoc @apollo/client/react!useMutation.DocumentationTypes.useMutation:call(1)}
       */
      <
        TData,
        TVariables extends OperationVariables = OperationVariables,
        TCache extends Cache.Implementation = Cache.Implementation,
        TConfiguredVariables extends Partial<TVariables> = {},
        TErrorPolicy extends ErrorPolicy | undefined = undefined,
      >(
        mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
        options?: useMutation.Options<
          NoInfer<TData>,
          NoInfer<TVariables>,
          TCache,
          {
            [K in keyof TConfiguredVariables]: K extends keyof TVariables ?
              TConfiguredVariables[K]
            : never;
          }
        > &
          (TErrorPolicy extends undefined ? {}
          : {
              /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#errorPolicy:member} */
              errorPolicy: TErrorPolicy;
            })
      ): useMutation.ResultTuple<
        TData,
        MakeRequiredVariablesOptional<TVariables, TConfiguredVariables>,
        TCache,
        TErrorPolicy
      >;
    }

    /** {@inheritDoc @apollo/client/react!useMutation.DocumentationTypes.useMutation:call(1)} */
    export interface Modern {
      /** {@inheritDoc @apollo/client/react!useMutation.DocumentationTypes.useMutation:call(1)} */
      <
        TData,
        TVariables extends OperationVariables,
        // this overload should never be manually defined, it should always be inferred
        TOptions extends never,
      >(
        mutation: DocumentNode | TypedDocumentNode<TData, TVariables>
      ): useMutation.ResultForOptions<
        TData,
        TVariables,
        Cache.Implementation,
        Record<string, never>
      >;

      /** {@inheritDoc @apollo/client/react!useMutation.DocumentationTypes.useMutation:call(1)} */
      <
        TData,
        TVariables extends OperationVariables,
        // this overload should never be manually defined, it should always be inferred
        TOptions extends useMutation.Options<
          NoInfer<TData>,
          NoInfer<TVariables>,
          Cache.Implementation
        > & {
          variables?: {
            [K in Exclude<
              keyof TOptions["variables"],
              keyof TVariables
            >]?: never;
          };
        },
        TErrorPolicy extends ErrorPolicy | undefined = undefined,
      >(
        mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
        options?: TOptions &
          useMutation.OptionsFor<TOptions> & {
            /** {@inheritDoc @apollo/client!MutationOptionsDocumentation#errorPolicy:member} */
            errorPolicy?: TErrorPolicy;
          }
      ): useMutation.ResultForOptions<
        TData,
        TVariables,
        Cache.Implementation,
        TOptions,
        TErrorPolicy
      >;
    }

    export type Evaluated = SignatureStyle extends "classic" ? Classic : Modern;
  }

  /** {@inheritDoc @apollo/client/react!useMutation.DocumentationTypes.useMutation:call(1)} */
  export interface Signature extends Signatures.Evaluated {}
}

/** {@inheritDoc @apollo/client/react!useMutation.DocumentationTypes.useMutation:call(1)} */
export const useMutation: useMutation.Signature = function useMutation<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
  TCache extends Cache.Implementation = Cache.Implementation,
  TConfiguredVariables extends Partial<TVariables> = {},
>(
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: useMutation.Options<
    NoInfer<TData>,
    NoInfer<TVariables>,
    TCache,
    {
      [K in keyof TConfiguredVariables]: K extends keyof TVariables ?
        TConfiguredVariables[K]
      : never;
    }
  >
): useMutation.ResultTuple<
  TData,
  MakeRequiredVariablesOptional<TVariables, TConfiguredVariables>,
  TCache
> {
  const client = useApolloClient(options?.client);
  const [result, setResult] = React.useState<
    Omit<useMutation.Result<TData>, "reset">
  >(() => createInitialResult(client));

  const ref = React.useRef({
    result,
    mutationId: 0,
    isMounted: true,
    client,
    mutation,
    options,
  });

  useIsomorphicLayoutEffect(() => {
    Object.assign(ref.current, { client, options, mutation });
  });

  const execute = React.useCallback(
    (
      executeOptions: useMutation.MutationFunctionOptions<
        TData,
        TVariables,
        TCache
      > = {} as useMutation.MutationFunctionOptions<TData, TVariables, TCache>
    ) => {
      const { options, mutation } = ref.current;
      const baseOptions = { ...options, mutation };
      const client = executeOptions.client || ref.current.client;
      const context =
        typeof executeOptions.context === "function" ?
          executeOptions.context(options?.context)
        : executeOptions.context;

      if (!ref.current.result.loading && ref.current.isMounted) {
        setResult(
          (ref.current.result = {
            loading: true,
            error: undefined,
            data: undefined,
            called: true,
            client,
          })
        );
      }

      const mutationId = ++ref.current.mutationId;
      const clientOptions = mergeOptions(baseOptions, {
        ...executeOptions,
        context,
      } as any);

      return preventUnhandledRejection(
        client
          .mutate(
            clientOptions as ApolloClient.MutateOptions<
              TData,
              OperationVariables
            >
          )
          .then(
            (response) => {
              const { data, error } = response;

              const onError =
                executeOptions.onError || ref.current.options?.onError;

              if (error && onError) {
                onError(error, clientOptions);
              }

              if (mutationId === ref.current.mutationId) {
                const result = {
                  called: true,
                  loading: false,
                  data,
                  error,
                  client,
                };

                if (
                  ref.current.isMounted &&
                  !equal(ref.current.result, result)
                ) {
                  setResult((ref.current.result = result));
                }
              }

              const onCompleted =
                executeOptions.onCompleted || ref.current.options?.onCompleted;

              if (!error) {
                onCompleted?.(response.data!, clientOptions);
              }

              return response;
            },
            (error) => {
              if (
                mutationId === ref.current.mutationId &&
                ref.current.isMounted
              ) {
                const result = {
                  loading: false,
                  error,
                  data: void 0,
                  called: true,
                  client,
                };

                if (!equal(ref.current.result, result)) {
                  setResult((ref.current.result = result));
                }
              }

              const onError =
                executeOptions.onError || ref.current.options?.onError;

              if (onError) {
                onError(error, clientOptions);
              }

              throw error;
            }
          )
      );
    },
    []
  );

  const reset = React.useCallback(() => {
    if (ref.current.isMounted) {
      const result = createInitialResult(ref.current.client);
      Object.assign(ref.current, { mutationId: 0, result });
      setResult(result);
    }
  }, []);

  React.useEffect(() => {
    const current = ref.current;
    current.isMounted = true;

    return () => {
      current.isMounted = false;
    };
  }, []);

  return [execute as any, { reset, ...result }];
} as any;

function createInitialResult(client: ApolloClient) {
  return {
    data: undefined,
    error: undefined,
    called: false,
    loading: false,
    client,
  };
}
